'use client';

import clsx from 'clsx';
import { createElement, type ForwardedRef, type ElementRef, type JSX } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { BaseTypographyProps, TextStyleOptions } from './types.js';
import { textStyle } from './utils.js';

type TextElement = Exclude<keyof JSX.IntrinsicElements, 'a'>;

export interface TextProps<T extends TextElement> extends BaseTypographyProps, TextStyleOptions {
  /** The HTML element to render as. */
  as: T;
}

function Text<T extends TextElement>(
  {
    children,
    as,
    variant,
    fontStyle,
    alignment,
    textDecoration,
    tone,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: TextProps<T>,
  ref: ForwardedRef<ElementRef<T>>,
) {
  return createElement(
    as,
    {
      ...props,
      ref,
      className: clsx(textStyle({ variant, fontStyle, alignment, textDecoration, tone }), UNSAFE_className),
      style: UNSAFE_style,
    },
    children,
  );
}

/**
 * Renders the provided text contents in a specified style.
 *
 * See [typography guide](https://ui.cimpress.io/foundations/typography/).
 */
const _Text = withStyleProps(forwardRef(Text), 'Text');

export { _Text as Text };
